import {
  type Bed,
  type BedKey,
  BedKeySchema,
  DEFAULT_LOCALE,
  DELETION_FLAG,
  ERROR_CODE,
  type GroupKey,
  GroupKeySchema,
  UniversalError,
  type UserKey,
  isDisabled,
  isEnabled,
} from "@packages/core";
import { useForm } from "@tanstack/react-form";
import { Link, createFileRoute, useBlocker, useNavigate } from "@tanstack/react-router";
import { xor } from "es-toolkit";
import { useMemo } from "react";
import { LuChevronsLeft, LuChevronsRight, LuTrash } from "react-icons/lu";
import { useAsyncFn } from "react-use";
import { EmptyError, from, lastValueFrom, mergeMap } from "rxjs";
import * as v from "valibot";

import {
  Breadcrumbs,
  ButtonStyle,
  FormField,
  FormFieldInput,
  FormFieldInputStyle,
  PageName,
  PaperStyle,
  Spinner,
  TableStyle,
  TypographyStyle,
  showError,
} from "#components";
import {
  $alert,
  $confirm,
  findBeds,
  modifyBed,
  modifyGroup,
  removeGroupInfiniteQueryCache,
  resetBedInfiniteQueries,
  updateGroupQueryCache,
  useBedInfiniteQuery,
  useFacilityQuery,
  useGetErrorMessage,
  useGroupQuery,
  useResidentQuery,
  useSm,
  useTranslate,
} from "#services";
import { css } from "#styled-system/css";
import { token } from "#styled-system/tokens";

export const Route = createFileRoute("/_authenticated/facilities/$facilityKey/groups/$groupKey")({
  component: Component,
  params: {
    parse(parameters) {
      return v.parse(v.object({ groupKey: GroupKeySchema() }), parameters);
    },
  },
});

const classes = TableStyle.raw({ foot: false });

function Component() {
  const { facilityKey, groupKey } = Route.useParams();

  const { data: group } = useGroupQuery(groupKey);
  const { data: facility } = useFacilityQuery(facilityKey);
  const getErrorMessage = useGetErrorMessage();
  const navigate = useNavigate();
  const translate = useTranslate();
  const bedInfiniteQuery = useBedInfiniteQuery({
    index: "facilityKey-deleted-gsi",
    condition: [
      ["facilityKey", "=", facilityKey],
      ["deleted", "=", DELETION_FLAG.DEFAULT],
    ],
  });
  const sm = useSm();

  const beds = useMemo(
    function () {
      return (
        bedInfiniteQuery.data?.pages
          .flatMap(function (page) {
            return page.beds;
          })
          .sort(function (a, b) {
            return (
              a.roomName.localeCompare(b.roomName) ||
              a.roomPronunciation.localeCompare(b.roomPronunciation) ||
              a.bedName.localeCompare(b.bedName) ||
              a.bedPronunciation.localeCompare(b.bedPronunciation) ||
              a.bedKey.localeCompare(b.bedKey)
            );
          })
          .reduce(function (beds, bed) {
            return beds.set(bed.bedKey, bed);
          }, new Map<BedKey, Bed>()) ?? new Map<BedKey, Bed>()
      );
    },
    [bedInfiniteQuery.data],
  );

  const formSchema = useMemo(function () {
    return v.object({
      bedKeys: v.pipe(
        v.array(v.message(BedKeySchema(), ERROR_CODE.UNEXPECTED), ERROR_CODE.UNEXPECTED),
        v.check(function (elements) {
          // 重複が無い事を保証する。
          return new Set(elements).size === elements.length;
        }, ERROR_CODE.UNEXPECTED),
      ),
      name: v.pipe(
        v.string(ERROR_CODE.INPUT_REQUIRED),
        v.minLength(1, ERROR_CODE.INPUT_REQUIRED),
        v.maxGraphemes(128, ERROR_CODE.TOO_MANY_CHARACTERS),
      ),
    });
  }, []);
  const defaultValues = useMemo(
    function (): v.InferInput<typeof formSchema> {
      return {
        bedKeys: Array.from(beds.values())
          .filter(function (bed) {
            return bed.groupKey === groupKey;
          })
          .map(function (bed) {
            return bed.bedKey;
          }),
        name: group?.name ?? "",
      };
    },
    [beds, group, groupKey],
  );

  const form = useForm({
    defaultValues,
    async onSubmit({ value: input }) {
      if (!facility) {
        showError(new UniversalError(ERROR_CODE.NOT_LOADED));
        return;
      }

      let output;
      try {
        output = v.parse(formSchema, input);
      } catch (error) {
        console.error(error);
        showError(new UniversalError(ERROR_CODE.INVALID_FORM));
        return;
      }

      console.debug("フォームが送信されました。", output);

      if (
        output.bedKeys.length < 10 &&
        !(await $confirm({
          color: "warning",
          content: translate("%GroupForm.14%"),
          no: translate("%Back.0%"),
          title: translate("%GroupForm.13%"),
          yes: translate("%GroupForm.15%"),
        }))
      ) {
        return;
      }

      let group;
      try {
        console.debug("グループ[" + groupKey + "]を変更します。");
        const response = await modifyGroup({ ...output, groupKey });
        group = response.group;
      } catch (error) {
        showError(error);
        return;
      }

      const bedKeys = xor(output.bedKeys, defaultValues.bedKeys as Array<BedKey>);
      try {
        await lastValueFrom(
          from(bedKeys).pipe(
            mergeMap(async function (bedKey) {
              const groupKey = output.bedKeys.includes(bedKey) ? group.groupKey : facility.groupKey;
              try {
                console.debug("ベッド[" + bedKey + "]をグループ[" + groupKey + "]に所属させます。");
                await modifyBed({ bedKey, groupKey });
                console.debug(
                  "ベッド[" + bedKey + "]をグループ[" + groupKey + "]に所属させました。",
                );
              } catch (error) {
                showError(error);
                console.warn(
                  "ベッド[" +
                    bedKey +
                    "]のグループ[" +
                    groupKey +
                    "]への所属させられませんでした。",
                  error,
                );
              }
            }, 8),
          ),
        );
      } catch (error) {
        if (error instanceof EmptyError) {
          console.debug("変更されたベッドはありません。");
        } else {
          showError(error);
        }
      }

      await $alert({
        color: "success",
        content: translate("%GroupForm.12%"),
        title: translate("%Complete.0%"),
      });

      resetBedInfiniteQueries();
      removeGroupInfiniteQueryCache();
      updateGroupQueryCache(group.groupKey, group);

      await navigate({
        ignoreBlocker: true,
        to: "/facilities/$facilityKey/groups",
        params: { facilityKey },
      });
    },
    async onSubmitInvalid() {
      await $alert({
        color: "error",
        content: translate("%Form.3%"),
        title: translate("%Form.2%"),
      });
    },
    validators: { onSubmit: formSchema },
  });

  useBlocker({
    async shouldBlockFn() {
      return (
        (!form.store.state.isDefaultValue || form.store.state.isSubmitting) &&
        !(await $confirm({
          color: "warning",
          content: translate("%Form.1%"),
          no: translate("%Stay.0%"),
          title: translate("%Form.0%"),
          yes: translate("%Leave.0%"),
        }))
      );
    },
  });

  return (
    <form
      className={css({
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        minHeight: 0,
        padding: { smDown: "12px 16px", sm: "16px 24px" },
      })}
      data-main
      data-maximized={sm ? true : undefined}
      onSubmit={function (event) {
        event.preventDefault();
        event.stopPropagation();
        form.handleSubmit();
      }}
    >
      <PageName>
        {translate("%Add.3%", { $: { name: translate("%Group.0%") } }) +
          " - " +
          (facility ? facility.name : translate("%Loading.0%"))}
      </PageName>

      <div
        className={css({
          alignItems: "center",
          display: "flex",
          gap: "8px",
          justifyContent: { smDown: "end", sm: "space-between" },
        })}
      >
        <Breadcrumbs
          className={css(
            TypographyStyle.raw({ fontSize: "14px", fontWeight: 500, lineHeight: 1 }),
            { display: "flex", hideBelow: "sm" },
          )}
        >
          <Link
            className={TypographyStyle({
              color: "muted",
              fontSize: "14px",
              lineHeight: 1,
              link: true,
            })}
            params={{ facilityKey }}
            to="/facilities/$facilityKey/groups"
          >
            {translate("%GroupManagement.0%")}
          </Link>
          <p className={TypographyStyle({ color: "default", fontSize: "14px", lineHeight: 1 })}>
            {group?.name ?? translate("%Loading.0%")}
          </p>
        </Breadcrumbs>

        <div className={css({ display: "flex", gap: "8px", minWidth: 0 })}>
          <Link
            className={ButtonStyle({
              appearance: "ghost",
              borderRadius: "4px",
              color: "ghost",
              fontSize: "14px",
              gap: "8px",
              minWidth: 0,
              padding: "10px 16px",
            })}
            params={{ facilityKey }}
            to="/facilities/$facilityKey/groups"
          >
            <span className={TypographyStyle({ ellipsis: true })}>{translate("%Cancel.0%")}</span>
          </Link>
          <form.Subscribe
            selector={function (state) {
              return state.isSubmitting;
            }}
          >
            {function (submitting) {
              return (
                <button
                  className={ButtonStyle({
                    appearance: "contained",
                    borderRadius: "4px",
                    color: "primary",
                    fontSize: "14px",
                    gap: "8px",
                    minWidth: 0,
                    padding: "10px 16px",
                  })}
                  disabled={submitting || group?.deleted !== DELETION_FLAG.DEFAULT}
                  type="submit"
                >
                  <span className={TypographyStyle({ ellipsis: true })}>
                    {translate("%Save.0%")}
                  </span>
                </button>
              );
            }}
          </form.Subscribe>
        </div>
      </div>

      <div
        className={css(PaperStyle.raw({ borderRadius: "12px" }), {
          marginTop: { smDown: "12px", sm: "6px" },
          padding: { smDown: "16px", sm: "16px 24px" },
        })}
      >
        <h2
          className={TypographyStyle({
            ellipsis: true,
            fontSize: "18px",
            fontWeight: 600,
            lineHeight: "14 / 9",
          })}
        >
          {translate("%GroupForm.0%")}
        </h2>

        <form.Field name="name">
          {function (field) {
            return (
              <FormField
                caution={field.state.meta.errors
                  .map(function (error) {
                    return getErrorMessage(error);
                  })
                  .join("")}
                className={css({ marginTop: "16px", width: "stretch", maxWidth: { sm: "320px" } })}
                invalid={field.state.meta.errors.length > 0}
                label={translate("%GroupForm.1%")}
              >
                <FormFieldInput
                  className={FormFieldInputStyle({ width: "stretch" })}
                  id={field.name}
                  onBlur={function () {
                    field.handleBlur();
                  }}
                  onChange={function (event) {
                    field.handleChange(event.target.value);
                  }}
                  value={field.state.value}
                />
              </FormField>
            );
          }}
        </form.Field>
      </div>

      <div
        className={css(PaperStyle.raw({ borderRadius: "12px" }), {
          display: "grid",
          flexGrow: 1,
          minHeight: 0,
          gap: "16px",
          marginTop: "6px",
          gridTemplateColumns: { smDown: "1fr", sm: "repeat(2, 1fr)" },
          padding: { smDown: "16px", sm: "16px 24px" },
          '&[data-default-group="true"]': { display: "none" },
        })}
        data-default-group={groupKey === facility?.groupKey}
      >
        <div
          className={css({
            backgroundColor: "primary-foreground",
            borderRadius: "6px",
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            height: { smDown: "400px" },
            minHeight: 0,
            minWidth: 0,
            padding: "16px",
          })}
        >
          <h2
            className={TypographyStyle({
              ellipsis: true,
              fontSize: "18px",
              fontWeight: 600,
              lineHeight: "14 / 9",
            })}
          >
            {translate("%GroupForm.2%")}
          </h2>

          <p
            className={TypographyStyle({
              ellipsis: true,
              fontSize: "14px",
              lineHeight: "10 / 7",
              marginTop: "16px",
            })}
          >
            {translate("%GroupForm.3%")}
          </p>

          <div className={css({ flexGrow: 1, marginTop: "16px", minWidth: 0, overflowX: "auto" })}>
            <div
              className={css(
                classes.root,
                TypographyStyle.raw({ fontSize: "14px", lineHeight: 1 }),
                { height: "stretch" },
              )}
            >
              <div className={css(classes.head)}>
                <div
                  className={css(classes.headRow, {
                    gridTemplateColumns: "repeat(3, minmax(123px, 1fr)) 0.75fr",
                  })}
                >
                  <div
                    className={TypographyStyle({
                      color: "primary/60",
                      ellipsis: true,
                      fontWeight: 500,
                    })}
                  >
                    {translate("%Room.0%")}
                  </div>
                  <div
                    className={TypographyStyle({
                      color: "primary/60",
                      ellipsis: true,
                      fontWeight: 500,
                    })}
                  >
                    {translate("%Bed.0%")}
                  </div>
                  <div
                    className={TypographyStyle({
                      color: "primary/60",
                      ellipsis: true,
                      fontWeight: 500,
                    })}
                  >
                    {translate("%Resident.0%")}
                  </div>
                  <div />
                </div>
              </div>

              <div className={css(classes.body)}>
                <form.Subscribe
                  selector={function (state) {
                    return state.values.bedKeys;
                  }}
                >
                  {function (bedKeys) {
                    return Array.from(beds.values())
                      .filter(function (bed) {
                        return (
                          (bed.groupKey === groupKey || bed.groupKey === facility?.groupKey) &&
                          !bedKeys.includes(bed.bedKey)
                        );
                      })
                      .map(function (bed) {
                        return (
                          <div
                            className={css(classes.bodyRow, {
                              gridTemplateColumns: "repeat(3, minmax(123px, 1fr)) 0.75fr",
                            })}
                            key={bed.bedKey}
                          >
                            <div
                              className={TypographyStyle({
                                ellipsis: true,
                                fontSize: "14px",
                                lineHeight: "10 / 7",
                              })}
                            >
                              {bed.roomName}
                            </div>
                            <div
                              className={TypographyStyle({
                                ellipsis: true,
                                fontSize: "14px",
                                lineHeight: "10 / 7",
                              })}
                            >
                              {bed.bedName}
                            </div>
                            <ResidentNameCell userKey={bed.userKey} />
                            <div className={css({ textAlign: "right" })}>
                              <button
                                className={ButtonStyle({
                                  appearance: "outlined",
                                  borderRadius: "4px",
                                  color: "outlined",
                                  ellipsis: true,
                                  flexGrow: 1,
                                  fontSize: "14px",
                                  gap: "8px",
                                  padding: "12px",
                                })}
                                onClick={function () {
                                  form.setFieldValue("bedKeys", function (current) {
                                    return Array.from(new Set(current).add(bed.bedKey)).sort(
                                      function (bedKeyA, bedKeyB) {
                                        const a = beds.get(bedKeyA as BedKey);
                                        const b = beds.get(bedKeyB as BedKey);
                                        return (
                                          (a && b
                                            ? a.roomName.localeCompare(b.roomName) ||
                                              a.roomPronunciation.localeCompare(
                                                b.roomPronunciation,
                                              ) ||
                                              a.bedName.localeCompare(b.bedName) ||
                                              a.bedPronunciation.localeCompare(b.bedPronunciation)
                                            : 0) || bedKeyA.localeCompare(bedKeyB)
                                        );
                                      },
                                    );
                                  });
                                }}
                              >
                                <span className={TypographyStyle({ ellipsis: true })}>
                                  {translate("%Add.1%")}
                                </span>
                                <LuChevronsRight className={css({ fontSize: "16px" })} />
                              </button>
                            </div>
                          </div>
                        );
                      });
                  }}
                </form.Subscribe>
              </div>
            </div>
          </div>
        </div>

        <div
          className={css({
            backgroundColor: "primary-foreground",
            borderRadius: "6px",
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            height: { smDown: "400px" },
            minHeight: 0,
            minWidth: 0,
            padding: "16px",
          })}
        >
          <form.Subscribe
            selector={function (state) {
              return state.values.bedKeys.length;
            }}
          >
            {function (numberOfBeds) {
              return (
                <h2
                  className={TypographyStyle({
                    ellipsis: true,
                    fontSize: "18px",
                    fontWeight: 600,
                    lineHeight: "14 / 9",
                  })}
                >
                  {translate("%GroupForm.4%", {
                    $: { numberOfBeds: numberOfBeds.toLocaleString(DEFAULT_LOCALE) },
                  })}
                </h2>
              );
            }}
          </form.Subscribe>

          <form.Subscribe
            selector={function (state) {
              return state.values.bedKeys;
            }}
          >
            {function (bedKeys) {
              return bedKeys.length > 0 ? (
                <div
                  className={css({
                    flexGrow: 1,
                    marginTop: "16px",
                    minWidth: 0,
                    overflowX: "auto",
                  })}
                >
                  <div
                    className={css(
                      classes.root,
                      TypographyStyle.raw({ fontSize: "14px", lineHeight: 1 }),
                      { height: "stretch" },
                    )}
                  >
                    <div className={css(classes.head)}>
                      <div
                        className={css(classes.headRow, {
                          gridTemplateColumns: "0.75fr repeat(3, minmax(123px, 1fr))",
                        })}
                      >
                        <div />
                        <div
                          className={TypographyStyle({
                            color: "primary/60",
                            ellipsis: true,
                            fontWeight: 500,
                          })}
                        >
                          {translate("%Room.0%")}
                        </div>
                        <div
                          className={TypographyStyle({
                            color: "primary/60",
                            ellipsis: true,
                            fontWeight: 500,
                          })}
                        >
                          {translate("%Bed.0%")}
                        </div>
                        <div
                          className={TypographyStyle({
                            color: "primary/60",
                            ellipsis: true,
                            fontWeight: 500,
                          })}
                        >
                          {translate("%Resident.0%")}
                        </div>
                      </div>
                    </div>

                    <div className={css(classes.body)}>
                      {bedKeys.map(function (bedKey, index) {
                        const bed = beds.get(bedKey as BedKey);

                        return (
                          <div
                            className={css(classes.bodyRow, {
                              gridTemplateColumns: "0.75fr repeat(3, minmax(123px, 1fr))",
                            })}
                            key={bed?.bedKey ?? index}
                          >
                            <div>
                              <button
                                className={ButtonStyle({
                                  appearance: "outlined",
                                  borderRadius: "4px",
                                  color: "outlined",
                                  ellipsis: true,
                                  flexGrow: 1,
                                  fontSize: "14px",
                                  gap: "8px",
                                  padding: "12px",
                                })}
                                onClick={function () {
                                  form.setFieldValue("bedKeys", function (current) {
                                    return current.filter(function (bk) {
                                      return bk !== bedKey;
                                    });
                                  });
                                }}
                              >
                                <LuChevronsLeft className={css({ fontSize: "16px" })} />
                                <span className={TypographyStyle({ ellipsis: true })}>
                                  {translate("%Release.0%")}
                                </span>
                              </button>
                            </div>
                            <div
                              className={TypographyStyle({
                                ellipsis: true,
                                fontSize: "14px",
                                lineHeight: "10 / 7",
                              })}
                            >
                              {bed?.roomName}
                            </div>
                            <div
                              className={TypographyStyle({
                                ellipsis: true,
                                fontSize: "14px",
                                lineHeight: "10 / 7",
                              })}
                            >
                              {bed?.bedName}
                            </div>
                            <ResidentNameCell userKey={bed?.userKey} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className={css({
                    alignItems: "center",
                    display: "flex",
                    flexGrow: 1,
                    justifyContent: "center",
                  })}
                >
                  <p
                    className={TypographyStyle({
                      ellipsis: true,
                      fontSize: "14px",
                      lineHeight: "10 / 7",
                    })}
                  >
                    {translate("%GroupForm.5%")}
                  </p>
                </div>
              );
            }}
          </form.Subscribe>
        </div>
      </div>

      <div
        className={css({
          display: "flex",
          justifyContent: "center",
          marginTop: "6px",
          '&[data-default-group="true"]': { display: "none" },
        })}
        data-default-group={groupKey === facility?.groupKey}
      >
        <DeleteButton groupKey={groupKey} />
      </div>
    </form>
  );
}

function DeleteButton({ groupKey }: { groupKey?: GroupKey }) {
  const navigate = useNavigate();
  const translate = useTranslate();
  const { data: group } = useGroupQuery(groupKey);

  const { data: facility } = useFacilityQuery(group?.facilityKey);

  const [state, execute] = useAsyncFn(
    async function () {
      if (!group || !isEnabled(group) || !facility) {
        return;
      }

      if (
        !(await $confirm({
          color: isDisabled(group) ? "success" : "error",
          content: isDisabled(group) ? translate("%GroupForm.9%") : translate("%GroupForm.7%"),
          title: isDisabled(group) ? translate("%GroupForm.8%") : translate("%GroupForm.6%"),
        }))
      ) {
        return;
      }

      // 全てのベッドをグループから外す。
      try {
        const { beds } = await findBeds({
          index: "groupKey-deleted",
          condition: [["groupKey", "=", group.groupKey]],
        });
        await lastValueFrom(
          from(beds).pipe(
            mergeMap(async function ({ bedKey }) {
              try {
                console.debug(
                  "ベッド[" + bedKey + "]をグループ[" + facility.groupKey + "]に所属させます。",
                );
                await modifyBed({ bedKey, groupKey: facility.groupKey });
                console.debug(
                  "ベッド[" + bedKey + "]をグループ[" + facility.groupKey + "]に所属させました。",
                );
              } catch (error) {
                showError(error);
                console.warn(
                  "ベッド[" +
                    bedKey +
                    "]のグループ[" +
                    facility.groupKey +
                    "]への所属させられませんでした。",
                  error,
                );
              }
            }, 8),
          ),
          { defaultValue: null },
        );

        resetBedInfiniteQueries();
      } catch (error) {
        showError(error);
        return;
      }

      // グループを変更する。
      let newGroup;
      try {
        const response = await modifyGroup({
          deleted: DELETION_FLAG.ARCHIVED,
          groupKey: group.groupKey,
        });
        newGroup = response.group;
      } catch (error) {
        showError(error);
        return;
      }

      await $alert({
        color: isEnabled(newGroup) ? "success" : "error",
        content: isEnabled(newGroup) ? translate("%GroupForm.11%") : translate("%GroupForm.10%"),
        title: translate("%Complete.0%"),
      });

      updateGroupQueryCache(group.groupKey, newGroup);
      removeGroupInfiniteQueryCache();

      await navigate({
        ignoreBlocker: true,
        params: { facilityKey: group.facilityKey },
        to: "/facilities/$facilityKey/groups",
      });
    },
    [group],
  );

  return (
    <button
      className={ButtonStyle({
        appearance: "ghost",
        borderRadius: "4px",
        color: "ghost",
        fontSize: "14px",
        gap: "8px",
        minWidth: 0,
        padding: "10px 16px",
      })}
      disabled={!group || !isEnabled(group) || state.loading}
      onClick={function () {
        execute();
      }}
    >
      {state.loading ? (
        <Spinner color={token("colors.foreground")} size="16px" type="a" />
      ) : (
        <LuTrash className={css({ fontSize: "16px" })} />
      )}
      <span className={TypographyStyle({ ellipsis: true })}>{translate("%GroupForm.6%")}</span>
    </button>
  );
}

function ResidentNameCell({ userKey }: { userKey?: UserKey | null }) {
  const residentQuery = useResidentQuery(userKey);
  return (
    <div className={TypographyStyle({ ellipsis: true, fontSize: "14px", lineHeight: "10 / 7" })}>
      {residentQuery.data?.name}
    </div>
  );
}
